import {
    Col,
    DeleteButton,
    EditButton,
    Form,
    getDefaultSortOrder,
    Icons,
    Image,
    Input,
    List,
    Row,
    Select,
    Space,
    Switch,
    Table,
    TagField,
    TextField,
    useTable,
} from '@pankod/refine-antd';
import { CrudFilters, IResourceComponentsProps, useSelect } from '@pankod/refine-core';
import type { CheckboxValueType } from 'antd/es/checkbox/Group';
import type { ColumnsType } from 'antd/es/table';
import { Button } from 'antd';
import { ButtonColumn } from 'components/buttonColumn';
import { ButtonExport } from 'components/buttonExport';
import { ButtonFilter } from 'components/buttonFilter';
import useMediaScreen from 'hooks/useMediaScreen';
import React, { useState } from 'react';
import { IUser, IUserResponse } from 'types/user.interface';
import { formatDateTime, getOrderNumber, includesClassName } from 'utils';
import { loginAs } from 'apis/auth';
import { message } from 'antd';

export const UserList: React.FC<IResourceComponentsProps> = () => {
    const { options: labels } = useSelect<any>({
        resource: 'labels',
        fetchSize: 100,
        optionLabel: 'name',
        optionValue: '_id',
    });
    const { isMobile } = useMediaScreen();

    const optionsRole = [
        {
            label: 'All role',
            value: '',
        },
        {
            label: 'Admin',
            value: 'admin',
        },
        {
            label: 'User',
            value: 'user',
        },
        {
            label: 'Contributor',
            value: 'contributor',
        },
    ];

    const { tableProps, sorter, searchFormProps }: any = useTable<IUserResponse>({
        resource: 'users',
        initialPageSize: 100,
        onSearch: (params) => {
            const filters: CrudFilters = [];
            const input: any = params;
            filters.push({
                field: 'search',
                operator: 'in',
                value: { search: input.search || '', labelId: input.labelId || '', role: input.role || '', isBlocked: input.isBlocked || false },
            });
            return filters;
        },
    });

    const current = tableProps?.pagination?.current;
    const pageSize = tableProps?.pagination?.pageSize;

    const defaultColumn: CheckboxValueType[] = ['username', 'email', 'lastLogin', 'isContributor', 'purchasedTokensRemaining', 'starsRemaining', 'createdAt'];

    const [hideCols, setHideCols] = useState<CheckboxValueType[]>(defaultColumn);

    const handleLoginAs = async (userId: string) => {
        try {
            const data = await loginAs(userId);
            if (data?.accessToken) {
                // Get the web app URL from environment or use default
                const webAppUrl = process.env.REACT_APP_WEB_URL || 'https://app-dev.crooklynclan.net';
                // Redirect to web app with admin token (using adminToken to avoid conflict with email verification token)
                window.open(`${webAppUrl}/auth/login?adminToken=${data.accessToken}`, '_blank');
                message.success('Redirecting to login as user...');
            } else {
                message.error('Failed to get access token');
            }
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Failed to login as user');
        }
    };

    const columns: ColumnsType<IUser> = [
        {
            title: 'Index',
            dataIndex: '_id',
            key: '_id',
            render: (value, item, index) => <TextField value={getOrderNumber(current, pageSize, index)} />,
        },
        {
            title: 'Avatar',
            dataIndex: 'avatar',
            key: 'avatar',
            render: (value) => <div>{value && <Image width={50} src={value} />}</div>,
            className: includesClassName(hideCols, 'avatar'),
        },

        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
            render: (_, record) => <TextField value={record?.username || ''} />,
            className: includesClassName(hideCols, 'username'),
            defaultSortOrder: getDefaultSortOrder('username', sorter),
            sorter,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (value) => <TagField value={value} />,
            className: includesClassName(hideCols, 'email'),
            defaultSortOrder: getDefaultSortOrder('email', sorter),
            sorter,
        },
        {
            title: 'First Name',
            dataIndex: 'firstName',
            key: 'firstName',
            render: (value) => <TextField value={value} />,
            className: includesClassName(hideCols, 'firstName'),
            defaultSortOrder: getDefaultSortOrder('firstName', sorter),
            sorter,
        },
        {
            title: 'Last Name',
            dataIndex: 'lastName',
            key: 'lastName',
            render: (value) => <TextField value={value} />,
            className: includesClassName(hideCols, 'lastName'),
            defaultSortOrder: getDefaultSortOrder('lastName', sorter),
            sorter,
        },
        {
            title: 'Gender',
            dataIndex: 'gender',
            key: 'gender',
            render: (value) => <TextField value={value} />,
            className: includesClassName(hideCols, 'gender'),
            defaultSortOrder: getDefaultSortOrder('gender', sorter),
            sorter,
        },
        {
            title: 'Role',
            dataIndex: 'isContributor',
            key: 'isContributor',
            render: (_, record) => <TextField value={record?.role === 'admin' ? 'Admin' : record.isContributor ? 'Contributor' : 'User'} />,
            className: includesClassName(hideCols, 'isContributor'),
            defaultSortOrder: getDefaultSortOrder('isContributor', sorter),
            sorter,
        },
        // {
        //   title: "Purchased",
        //   dataIndex: "purchasedTokensRemaining",
        //   key: "purchasedTokensRemaining",
        //   render: (value) => <TextField value={value} />,
        //   className: includesClassName(hideCols, "purchasedTokensRemaining"),
        //   defaultSortOrder: getDefaultSortOrder("purchasedTokensRemaining", sorter),
        //   sorter,
        // },
        {
            title: 'Stars',
            dataIndex: 'starsRemaining',
            key: 'starsRemaining',
            render: (value) => <TextField value={value} />,
            className: includesClassName(hideCols, 'starsRemaining'),
            defaultSortOrder: getDefaultSortOrder('starsRemaining', sorter),
            sorter,
        },
        {
            title: 'CreatedAt',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (value) => <TextField value={formatDateTime(value)} />,
            className: includesClassName(hideCols, 'createdAt'),
            defaultSortOrder: getDefaultSortOrder('createdAt', sorter),
            sorter,
        },
        {
            title: 'Last Login',
            dataIndex: 'lastLogin',
            key: 'lastLogin',
            render: (value) => <TextField value={formatDateTime(value)} />,
            className: includesClassName(hideCols, 'lastLogin'),
            defaultSortOrder: getDefaultSortOrder('lastLogin', sorter),
            sorter,
        },

        {
            title: 'Mobile Number',
            dataIndex: 'mobileNumber',
            key: 'mobileNumber',
            render: (value) => <TextField value={value} />,
            className: includesClassName(hideCols, 'mobileNumber'),
            defaultSortOrder: getDefaultSortOrder('mobileNumber', sorter),
            sorter,
        },
        {
            title: 'Confirmed',
            dataIndex: 'isConfirmed',
            key: 'isConfirmed',
            render: (value) => <TextField value={value ? 'Yes' : 'No'} />,
            className: includesClassName(hideCols, 'isConfirmed'),
            defaultSortOrder: getDefaultSortOrder('isConfirmed', sorter),
            sorter,
        },
        {
            title: 'Active',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (value) => <TextField value={value ? 'Yes' : 'No'} />,
            className: includesClassName(hideCols, 'isActive'),
            defaultSortOrder: getDefaultSortOrder('isActive', sorter),
            sorter,
        },
        {
            title: 'Cash Out Blocked',
            dataIndex: 'isCashOutBlocked',
            key: 'isCashOutBlocked',
            render: (value) => <TextField value={value ? 'Yes' : 'No'} />,
            className: includesClassName(hideCols, 'isCashOutBlocked'),
            defaultSortOrder: getDefaultSortOrder('isCashOutBlocked', sorter),
            sorter,
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <EditButton hideText size='small' recordItemId={record._id} />
                    <DeleteButton hideText size='small' recordItemId={record._id} />
                    <Button
                        type="primary"
                        size="small"
                        icon={<Icons.LoginOutlined />}
                        onClick={() => handleLoginAs(record._id)}
                        title="Login as this user"
                    >
                        Login as
                    </Button>
                </Space>
            ),
        },
    ];

    const options = [
        { label: 'Avatar', value: 'avatar' },
        { label: 'Username', value: 'username' },
        { label: 'Email', value: 'email' },
        { label: 'First Name', value: 'firstName' },
        { label: 'Last Name', value: 'lastName' },
        { label: 'Gender', value: 'gender' },
        { label: 'Role', value: 'isContributor' },
        // { label: "Purchased", value: "purchasedTokensRemaining" },
        { label: 'Stars', value: 'starsRemaining' },
        { label: 'Created', value: 'createdAt' },
        { label: 'LastLogin', value: 'lastLogin' },
        { label: 'Mobile Number', value: 'mobileNumber' },
        { label: 'Confirmed', value: 'isConfirmed' },
        { label: 'Active', value: 'isActive' },
        { label: 'Cash Out Blocked', value: 'isCashOutBlocked' },
    ];

    return (
        <List>
            <Row style={{ display: 'block' }}>
                <div style={{ display: 'flex', gap: '5px' }}>
                    <TextField value='Total:' style={{ color: '#626262', fontSize: '14px', fontWeight: 700 }} />
                    <TextField value={tableProps?.pagination?.total} style={{ color: '#626262', fontSize: '14px', fontWeight: 700 }} />
                </div>

                <Col>
                    <Form layout='vertical' {...searchFormProps}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'start', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row' }}>
                            <Form.Item label='Search' name='search' style={{ width: isMobile ? '100%' : '40%', marginRight: '15px' }} initialValue=''>
                                <Input placeholder='Username, Email' prefix={<Icons.SearchOutlined />} />
                            </Form.Item>
                            <Form.Item label='Label' name='labelId'>
                                <Select
                                    style={{ width: isMobile ? '100%' : '250px', marginRight: '15px' }}
                                    defaultValue={{ value: '', label: 'All label' }}
                                    options={[{ value: '', label: 'All label' }, ...labels]}
                                />
                            </Form.Item>
                            <Form.Item label='Role' name='role'>
                                <Select style={{ width: isMobile ? '100%' : '130px' }} defaultValue={{ value: '', label: 'All role' }} options={optionsRole} />
                            </Form.Item>
                            <Form.Item style={{ marginLeft: '15px' }} label='Blocked' name='isBlocked'>
                                <Switch defaultChecked={false} />
                            </Form.Item>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Form.Item>
                                <ButtonFilter />
                            </Form.Item>
                            <div>
                                <ButtonColumn options={options} setHideCols={setHideCols} defaultColumn={defaultColumn} />
                                <ButtonExport path='users' />
                            </div>
                        </div>
                    </Form>
                </Col>

                <Col>
                    <Table
                        {...tableProps}
                        pagination={{
                            ...tableProps?.pagination,
                            showSizeChanger: true,
                        }}
                        columns={columns}
                        rowKey='id'
                    />
                </Col>
            </Row>
        </List>
    );
};
